import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { buildSeo, humanizeSlug } from "@/lib/seo";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Layout } from "@/components/Layout";
import { gameOgImage } from "@/lib/og-images";
import { GameHistoryView } from "@/components/GameHistoryView";
import { RouteNotFound } from "@/components/RouteNotFound";
import { canonicalUrl } from "@/lib/seo-policy";
import { latestDrawSummary } from "@/lib/results-nav";

const searchSchema = z.object({
  page: fallback(z.number().int(), 1).default(1),
});

export const Route = createFileRoute("/games/$gameSlug/history")({
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [stripSearchParams({ page: 1 })],
  },
  head: ({ params, match }) => {
    const name = humanizeSlug(params.gameSlug);
    const searchObj = (match as { search?: { page?: number } } | undefined)?.search;
    const rawPage = searchObj?.page ?? 1;
    const page = Math.max(1, Math.floor(rawPage));
    const basePath = `/games/${params.gameSlug}/history`;
    const path = page > 1 ? `${basePath}?page=${page}` : basePath;
    const image = gameOgImage(params.gameSlug);
    const title = page > 1
      ? `${name} Winning Numbers History — Page ${page} | USA Lottery Club`
      : `${name} Winning Numbers History — Past Results Archive | USA Lottery Club`;
    const description = page > 1
      ? `Older ${name} winning numbers — page ${page} of the ${name} drawing archive.`
      : `Complete ${name} drawing history. Browse past winning numbers, multipliers, and draw dates in one searchable archive.`;
    const latest = latestDrawSummary(params.gameSlug);
    const socialTitle = latest
      ? `${name} winning numbers — latest draw ${latest.dateLabel}`
      : `${name} past winning numbers`;
    const socialDesc = latest && latest.numbersText
      ? `Most recent ${name} draw (${latest.dateLabel}): ${latest.numbersText}. Browse every past drawing by date.`
      : description;
    const seo = buildSeo({
      title,
      description,
      path,
      image,
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${name} Winning Numbers History`,
          url: path,
          about: { "@type": "Game", name: `${name} Lottery` },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "/" },
            { "@type": "ListItem", position: 2, name: name, item: `/games/${params.gameSlug}` },
            { "@type": "ListItem", position: 3, name: "History", item: path },
          ],
        },
      ],
    });
    return {
      ...seo,
      meta: [
        ...seo.meta,
        { property: "og:title", content: socialTitle },
        { property: "og:description", content: socialDesc },
        { name: "twitter:title", content: socialTitle },
        { name: "twitter:description", content: socialDesc },
        ...(latest ? [
          { name: "twitter:label1", content: "Latest draw" },
          { name: "twitter:data1", content: latest.dateLabel },
          { name: "twitter:label2", content: "Winning numbers" },
          { name: "twitter:data2", content: latest.numbersText || "See page" },
        ] : []),
      ],
      links: [
        ...seo.links,
        ...(page > 1
          ? [{ rel: "prev", href: page > 2 ? `${canonicalUrl(basePath)}?page=${page - 1}` : canonicalUrl(basePath) }]
          : []),
      ],
    };
  },
  component: HistoryPage,
  notFoundComponent: HistoryNotFound,
  errorComponent: HistoryNotFound,
});

function HistoryNotFound() {
  const { gameSlug } = Route.useParams();
  return (
    <Layout>
      <RouteNotFound gameSlug={gameSlug} />
    </Layout>
  );
}

function HistoryPage() {
  const { gameSlug } = Route.useParams();
  const { page } = Route.useSearch();
  return <GameHistoryView gameSlug={gameSlug} page={page ?? 1} />;
}
